/* eslint-disable unicorn/prefer-set-has */
import React, { useEffect, useRef } from 'react'

import {
  FormatInputValueFunction,
  InputAttributes,
  NumberFormatBaseProps,
  SourceType,
  Timeout,
} from './types'
import {
  caretUnknownFormatBoundary,
  charIsNumber,
  findChangeRange,
  geInputCaretPosition,
  getCaretPosInBoundary,
  getCaretPosition,
  noop,
  setCaretPosition,
  useInternalValues,
} from './utils'

function defaultRemoveFormatting(value: string) {
  return value.replace(/\D/g, '')
}

function defaultFormat(value: string) {
  return value
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useBaseMask<BaseType = InputAttributes>(
  props: NumberFormatBaseProps<BaseType>,
) {
  const {
    format = defaultFormat,
    removeFormatting = defaultRemoveFormatting,
    defaultValue,
    valueIsNumericString,
    onValueChange,
    isAllowed,
    onChange = noop,
    onKeyDown = noop,
    onMouseUp = noop,
    onFocus = noop,
    onBlur = noop,
    onPaste = noop,
    value: propValue,
    getCaretBoundary = caretUnknownFormatBoundary,
    isValidInputCharacter = charIsNumber,
    decimalSeparator,
  } = props
  const [{ formattedValue, numAsString }, onFormattedValueChange] =
    useInternalValues(
      propValue,
      defaultValue,
      Boolean(valueIsNumericString),
      format as FormatInputValueFunction,
      removeFormatting,
      onValueChange,
    )

  const lastUpdatedValue = useRef<string>()

  const _onValueChange: NumberFormatBaseProps['onValueChange'] = (
    values,
    source,
  ) => {
    lastUpdatedValue.current = values.formattedValue
    onFormattedValueChange(values, source)
  }

  useEffect(() => {
    const newFormattedValue = (format as FormatInputValueFunction)(numAsString)
    if (
      lastUpdatedValue.current === undefined ||
      newFormattedValue !== lastUpdatedValue.current
    ) {
      const input = focusedElm.current

      // eslint-disable-next-line unicorn/no-useless-undefined
      const _numAsString = removeFormatting(newFormattedValue, undefined)

      updateValue({
        formattedValue: newFormattedValue,
        numAsString: _numAsString,
        input,
        setCaretPosition: true,
        source: SourceType.props,
        event: undefined,
      })
    }
  })
  const focusedElm = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  const timeout = useRef<{
    setCaretTimeout: Timeout | null
    focusTimeout: Timeout | null
  }>({
    setCaretTimeout: null,
    focusTimeout: null,
  })

  useEffect(() => {
    return () => {
      clearTimeout(timeout.current.setCaretTimeout as unknown as Timeout)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      clearTimeout(timeout.current.focusTimeout as unknown as Timeout)
    }
  }, [])

  const _format = format as FormatInputValueFunction

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const getValueObject = (formattedValue: string, numAsString: string) => {
    const floatValue = Number.parseFloat(numAsString)

    return {
      formattedValue,
      value: numAsString,
      floatValue: Number.isNaN(floatValue) ? undefined : floatValue,
    }
  }

  const setPatchedCaretPosition = (
    el: HTMLInputElement | HTMLTextAreaElement,
    caretPos: number,
    currentValue: string,
  ) => {
    setCaretPosition(el, caretPos)
    timeout.current.setCaretTimeout = setTimeout(() => {
      if (el.value === currentValue) setCaretPosition(el, caretPos)
    }, 0)
  }

  const correctCaretPosition = (
    value: string,
    caretPos: number,
    direction?: string,
  ) => {
    return getCaretPosInBoundary(
      value,
      caretPos,
      getCaretBoundary(value),
      direction,
    )
  }

  const getNewCaretPosition = (
    inputValue: string,
    newFormattedValue: string,
    caretPos: number,
  ) => {
    const caretBoundary = getCaretBoundary(newFormattedValue)
    let updatedCaretPos = getCaretPosition(
      newFormattedValue,
      formattedValue,
      inputValue,
      caretPos,
      caretBoundary,
      isValidInputCharacter,
    )

    updatedCaretPos = getCaretPosInBoundary(
      newFormattedValue,
      updatedCaretPos,
      caretBoundary,
    )

    return updatedCaretPos
  }

  const updateValue = (params: {
    formattedValue?: string
    numAsString: string
    inputValue?: string
    input?: HTMLInputElement | HTMLTextAreaElement | null
    event?:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    source: SourceType
    caretPos?: number
    setCaretPosition?: boolean
  }) => {
    const {
      formattedValue: newFormattedValue = '',
      input,
      setCaretPosition = true,
      source,
      event,
      numAsString,
    } = params
    let { caretPos } = params

    if (input) {
      if (caretPos === undefined && setCaretPosition) {
        const inputValue = params.inputValue || input.value

        const currentCaretPosition = geInputCaretPosition(input)

        input.value = newFormattedValue

        caretPos = getNewCaretPosition(
          inputValue,
          newFormattedValue,
          currentCaretPosition,
        )
      }

      input.value = newFormattedValue

      if (setCaretPosition && caretPos !== undefined) {
        setPatchedCaretPosition(input, caretPos, newFormattedValue)
      }
    }

    if (newFormattedValue !== formattedValue) {
      _onValueChange(getValueObject(newFormattedValue, numAsString), {
        event,
        source,
      })
    }
  }

  const formatInputValue = (
    inputValue: string,
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    source: SourceType,
  ) => {
    const changeRange = findChangeRange(formattedValue, inputValue)
    const changeMeta = {
      ...changeRange,
      lastValue: formattedValue,
    }

    let _numAsString = removeFormatting(inputValue, changeMeta)
    const _formattedValue = _format(_numAsString)
    // eslint-disable-next-line unicorn/no-useless-undefined
    _numAsString = removeFormatting(_formattedValue, undefined)
    if (
      isAllowed &&
      !isAllowed(getValueObject(_formattedValue, _numAsString))
    ) {
      const input = event.target as HTMLInputElement | HTMLTextAreaElement
      const currentCaretPosition = geInputCaretPosition(input)

      const caretPos = getNewCaretPosition(
        inputValue,
        formattedValue,
        currentCaretPosition,
      )
      setPatchedCaretPosition(input, caretPos, formattedValue)
      return false
    }

    updateValue({
      formattedValue: _formattedValue,
      numAsString: _numAsString,
      inputValue,
      event,
      source,
      setCaretPosition: true,
      input: event.target as HTMLInputElement | HTMLTextAreaElement,
    })

    return true
  }

  const _onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const el = e.target
    const inputValue = el.value
    const changed = formatInputValue(inputValue, e, SourceType.event)
    if (changed) onChange(e)
  }

  const _onPaste = (
    e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    e.preventDefault()
    let inputValue = e.clipboardData.getData('text')
    const separators = [',', '.']
    if (decimalSeparator) {
      inputValue = [...inputValue]
        .map((item) => {
          if (separators.includes(item)) {
            return decimalSeparator
          }
          return item
        })
        .join('')
    }
    const changed = formatInputValue(inputValue, e, SourceType.event)
    if (changed) onPaste(e)
  }

  const _onKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const el = e.target as HTMLInputElement | HTMLTextAreaElement
    const { key } = e
    const { selectionStart, selectionEnd, value = '' } = el
    let expectedCaretPosition

    // eslint-disable-next-line unicorn/prefer-switch
    if (key === 'ArrowLeft' || key === 'Backspace') {
      expectedCaretPosition = Math.max((selectionStart as number) - 1, 0)
    } else if (key === 'ArrowRight') {
      expectedCaretPosition = Math.min(
        (selectionStart as number) + 1,
        value.length,
      )
    } else if (key === 'Delete') {
      expectedCaretPosition = selectionStart
    }

    if (
      expectedCaretPosition === undefined ||
      expectedCaretPosition === null ||
      selectionStart !== selectionEnd
    ) {
      onKeyDown(e)
      return
    }

    let newCaretPosition = expectedCaretPosition
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      const direction = key === 'ArrowLeft' ? 'left' : 'right'
      newCaretPosition = correctCaretPosition(
        value,
        expectedCaretPosition,
        direction,
      )
    } else if (
      key === 'Delete' &&
      !isValidInputCharacter(value[expectedCaretPosition])
    ) {
      newCaretPosition = correctCaretPosition(
        value,
        expectedCaretPosition,
        'right',
      )
    } else if (
      key === 'Backspace' &&
      !isValidInputCharacter(value[expectedCaretPosition])
    ) {
      newCaretPosition = correctCaretPosition(
        value,
        expectedCaretPosition,
        'left',
      )
    }
    if (newCaretPosition !== expectedCaretPosition) {
      setPatchedCaretPosition(el, newCaretPosition, value)
    }

    onKeyDown(e)
  }

  const _onMouseUp = (
    e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const el = e.target as HTMLInputElement | HTMLTextAreaElement

    const { selectionStart, selectionEnd, value = '' } = el

    if (selectionStart === selectionEnd) {
      const caretPosition = correctCaretPosition(
        value,
        selectionStart as number,
      )
      if (caretPosition !== selectionStart) {
        setPatchedCaretPosition(el, caretPosition, value)
      }
    }

    onMouseUp(e)
  }

  const _onFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.persist) e.persist()

    const el = e.target
    focusedElm.current = el

    timeout.current.focusTimeout = setTimeout(() => {
      const { selectionStart, selectionEnd, value = '' } = el

      const caretPosition = correctCaretPosition(
        value,
        selectionStart as number,
      )

      if (
        caretPosition !== selectionStart &&
        !(selectionStart === 0 && selectionEnd === value.length)
      ) {
        setPatchedCaretPosition(el, caretPosition, value)
      }

      onFocus(e)
    }, 0)
  }

  const _onBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    focusedElm.current = null
    clearTimeout(timeout.current.focusTimeout as unknown as Timeout)
    clearTimeout(timeout.current.setCaretTimeout as unknown as Timeout)
    onBlur(e)
  }

  return {
    value: formattedValue,

    onChange: _onChange,
    onPaste: _onPaste,
    onKeyDown: _onKeyDown,
    onMouseUp: _onMouseUp,
    onFocus: _onFocus,
    onBlur: _onBlur,
  }
}
